import { ComponentRef, inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { TranslateService } from '@ngx-translate/core';
import { resolveInternalAppLink } from '@shared/helpers/functions/resolve-internal-app-link';
import { ModalService } from '@shared/services';
import {
  create,
  createOptions,
  CustomCommandExecutedEventArgs,
  DocumentFormat,
  FileTabItemId,
  HyperlinkClickEventArgs,
  HyperlinkInfo,
  InsertTabItemId,
  Interval,
  KeyboardEventArgs,
  Options,
  PageLayoutTabItemId,
  Ribbon,
  RibbonButtonItem,
  RibbonItem,
  RibbonTab,
  RibbonTabType,
  RichEdit,
} from 'devexpress-richedit';
import { DocumentFormatApi } from 'devexpress-richedit/lib/client/model-api/formats/enum';
import { debounceTime, Observable, Subject, timer } from 'rxjs';
import { UnsaveChangeWarningComponent } from '../components/unsave-change-warning/unsave-change-warning.component';
import { DataMode } from '../helpers/enums/data-mode.enum';
import { InternalLinkInsertionRange } from '../helpers/static/internal-country-sections';
import { richEditorSetting } from '../helpers/static/settings';
import { SpellcheckerService } from './spellchecker.service';

const INSERT_INTERNAL_LINK_COMMAND = 'insertInternalLink';

@Injectable()
export class RichEditorService {
  private readonly modalService = inject(ModalService);
  private readonly spellcheckerService = inject(SpellcheckerService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly translateService = inject(TranslateService);

  private richEditor: RichEdit | undefined = undefined;
  private hasUnsavedChanges = false;
  private isInternalDocumentUpdate = false;
  private innerLinksEnabled = false;
  private richEditorSubject = new Subject<RichEdit>();
  private documentLoadedSubject = new Subject<RichEdit>();
  private innerLinksPickerSubject =
    new Subject<InternalLinkInsertionRange | null>();
  private richEditorKeyDownSubject = new Subject<KeyboardEvent>();

  richEditor$ = this.richEditorSubject.asObservable().pipe(debounceTime(350));
  onDocumentLoaded$ = this.documentLoadedSubject.asObservable();
  innerLinksPicker$ = this.innerLinksPickerSubject.asObservable();
  richEditorKeyDown$ = this.richEditorKeyDownSubject.asObservable();

  get isInEditionMode(): boolean {
    return this.richEditor?.readOnly ? false : true;
  }

  get hasFileUnsavedChanges(): boolean {
    return this.hasUnsavedChanges;
  }

  get isEmpty(): boolean {
    return !!(
      this.richEditor &&
      this.richEditor.document.getText().replace(/[\r\n\s]/g, '') === ''
    );
  }

  buildEditor(element: HTMLElement, viewType: DataMode): RichEditorService {
    const richEditor = create(element, this.buildEditorOptions(viewType));
    richEditor.documentSaveFormat = DocumentFormat.Html;
    richEditor.readOnly = viewType === DataMode.readonly;
    richEditor.showHorizontalRuler = viewType === DataMode.edition;
    richEditor.spellCheckerOptions.isEnabled = viewType === DataMode.edition;

    // # set events
    richEditor.events.documentLoaded.addHandler(
      this.onDocumentLoadedHandler.bind(this)
    );
    // RichEdit captures editor keyboard events inside its iframe, so Angular's
    // document-level keydown listener cannot observe them directly.
    richEditor.events.keyDown.addHandler(
      this.onRichEditorKeyDownHandler.bind(this)
    );
    richEditor.events.contentInserted.addHandler(
      this.onRichEditorContentChangedHandler.bind(this)
    );
    richEditor.events.contentRemoved.addHandler(
      this.onRichEditorContentChangedHandler.bind(this)
    );
    richEditor.events.characterPropertiesChanged.addHandler(
      this.onRichEditorContentChangedHandler.bind(this)
    );
    richEditor.events.paragraphPropertiesChanged.addHandler(
      this.onRichEditorContentChangedHandler.bind(this)
    );
    richEditor.events.hyperlinkClick.addHandler(
      this.onHyperlinkClickHandler.bind(this)
    );
    richEditor.events.customCommandExecuted.addHandler(
      (richEditor?: RichEdit, e?: CustomCommandExecutedEventArgs) => {
        if (e?.commandName === 'open' && richEditor) {
          richEditor.openDocument();
          this.hasUnsavedChanges = true;
        }

        if (
          e?.commandName === INSERT_INTERNAL_LINK_COMMAND &&
          richEditor &&
          this.innerLinksEnabled &&
          this.isInEditionMode
        ) {
          // A collapsed selection inserts the destination name. A non-empty
          // selection keeps its text and formatting when the link is applied.
          this.innerLinksPickerSubject.next({
            start: richEditor.selection.start,
            length: richEditor.selection.end - richEditor.selection.start,
          });
        }
      }
    );

    this.richEditor = richEditor;
    return this;
  }

  setFile(content: string, fileName: string): RichEditorService {
    if (this.richEditor) {
      this.hasUnsavedChanges = false;
      this.isInternalDocumentUpdate = true;
      const file = new File([content ? content : '<p></p>'], fileName, {
        type: 'text/html',
      });
      this.richEditor.openDocument(file, file.name, DocumentFormat.Html);
      timer(0).subscribe(() => this.richEditor?.focus());
    }
    return this;
  }

  /**
   * Applies a temporary yellow highlight to every matching text interval.
   */
  highlightText(query: string): void {
    const normalizedQuery = query.trim();
    if (!this.richEditor || !normalizedQuery) {
      return;
    }

    const documentText = this.richEditor.document.getText();
    const normalizedText = documentText.toLocaleLowerCase();
    const normalizedSearch = normalizedQuery.toLocaleLowerCase();
    let start = normalizedText.indexOf(normalizedSearch);

    this.isInternalDocumentUpdate = true;
    try {
      while (start !== -1) {
        this.richEditor.document.setCharacterProperties(
          { start, length: normalizedQuery.length },
          { highlightColor: '#fff176' }
        );
        start = normalizedText.indexOf(
          normalizedSearch,
          start + normalizedQuery.length
        );
      }
    } finally {
      this.isInternalDocumentUpdate = false;
    }
  }

  setMode(viewType: DataMode): void {
    if (this.richEditor) {
      this.richEditor.readOnly = viewType === DataMode.readonly;
      this.richEditor.showHorizontalRuler = viewType === DataMode.edition;
      this.richEditor.spellCheckerOptions.isEnabled =
        viewType === DataMode.edition;

      this.richEditor.updateRibbon((ribbons: Ribbon) => {
        ribbons.visible = viewType === DataMode.edition;
      });
      if (viewType === DataMode.edition) {
        timer(0).subscribe(() => {
          this.richEditor?.focus();
        });
      }
    }
  }

  openUnsaveChangeWarningModal(
    isDisabledSaveButton?: boolean
  ): ComponentRef<ModalComponent> {
    return this.modalService.open(
      UnsaveChangeWarningComponent,
      {
        appModalId: 'unsave-changes-warning-modal',
      },
      { disabledSaveButton: !!isDisabledSaveButton }
    );
  }

  destroy(): void {
    this.spellcheckerService.destroy();
    this.innerLinksPickerSubject.next(null);
    if (this.richEditor) {
      this.richEditor.dispose();
      this.richEditor = undefined;
      this.hasUnsavedChanges = false;
    }
  }

  loadSpellcheck(): Observable<void> {
    return this.spellcheckerService.init();
  }

  async extractHTML(callback: (html: string) => void): Promise<void> {
    if (this.richEditor !== undefined) {
      this.richEditor.exportToFile(async (file: File) => {
        const html: string = await file.text();
        callback(html);
      }, DocumentFormatApi.Html);
    }
  }

  exportToHtml(richEditor: RichEdit, callback: (html: string) => void): void {
    richEditor.exportToFile(async (file: File) => {
      const html: string = await file.text();
      callback(html);
    }, DocumentFormatApi.Html);
  }

  private onDocumentLoadedHandler(richEditor: RichEdit | undefined): void {
    if (richEditor) {
      this.isInternalDocumentUpdate = true;
      richEditor.document.setCharacterProperties(richEditor.document.interval, {
        fontName: 'Georgia',
      });
      richEditor.document.setDefaultCharacterProperties({
        size: 14,
      });
      this.hasUnsavedChanges = false;
      this.isInternalDocumentUpdate = false;
      this.richEditorSubject.next(richEditor);
      this.documentLoadedSubject.next(richEditor);
    }
  }

  setInnerLinksEnabled(enabled: boolean): void {
    this.innerLinksEnabled = enabled;
    if (!enabled) {
      this.innerLinksPickerSubject.next(null);
    }
  }

  insertInternalHyperlink(
    insertionRange: InternalLinkInsertionRange,
    label: string,
    url: string
  ): void {
    if (!this.innerLinksEnabled || !this.richEditor || !this.isInEditionMode) {
      return;
    }

    const editor = this.richEditor;
    const interval = new Interval(insertionRange.start, insertionRange.length);
    editor.beginUpdate();
    editor.history.beginTransaction();

    // DevExpress preserves an interval's existing result when display text is
    // empty. A collapsed interval instead needs the section name inserted.
    const hyperlink = insertionRange.length
      ? editor.document.hyperlinks.create(
          interval,
          new HyperlinkInfo('', url, '', label)
        )
      : editor.document.hyperlinks.create(
          insertionRange.start,
          new HyperlinkInfo(label, url, '', label)
        );
    editor.history.endTransaction();
    editor.endUpdate();
    editor.selection.setSelection(hyperlink.interval.end);
    this.hasUnsavedChanges = true;
    this.innerLinksPickerSubject.next(null);
    this.richEditorSubject.next(editor);
  }

  closeInnerLinksPicker(): void {
    this.innerLinksPickerSubject.next(null);
  }

  /**
   * GSTAR links stay inside the SPA; anything else keeps the editor default of
   * opening a new browser tab.
   */
  private onHyperlinkClickHandler(
    _richEditor?: RichEdit,
    e?: HyperlinkClickEventArgs
  ): void {
    const internalRoute = resolveInternalAppLink(e?.targetUri);

    if (!e || !internalRoute) {
      return;
    }

    e.handled = true;
    this.zone.run(() => this.router.navigateByUrl(internalRoute));
  }

  private onRichEditorKeyDownHandler(
    _richEditor?: RichEdit,
    event?: KeyboardEventArgs
  ): void {
    if (event?.htmlEvent) {
      this.richEditorKeyDownSubject.next(event.htmlEvent);
    }
  }

  private onRichEditorContentChangedHandler(
    richEditor: RichEdit | undefined
  ): void {
    if (richEditor && this.isInEditionMode) {
      if (this.isInternalDocumentUpdate) {
        return;
      }
      this.hasUnsavedChanges = true;
      this.richEditorSubject.next(richEditor);
    }
  }

  private buildEditorOptions(viewType: DataMode): Options {
    const options = createOptions();
    this.configBaseOptions(options);
    if (viewType === DataMode.readonly) {
      options.ribbon.visible = false;
    }
    this.configRibbon(options);
    return options;
  }

  private configBaseOptions(options: Options): void {
    options.width = richEditorSetting.width;
    options.height = richEditorSetting.height;
    options.unit = richEditorSetting.units;
    options.document!.format = richEditorSetting.format;
    options.fonts = {
      fonts: [{ name: 'Georgia', fontFamily: 'Georgia, serif' }],
      mappings: {
        defaultFontName: 'Georgia',
        rules: [{ sourceFontFamily: '*', destinationFontName: 'Georgia' }],
      },
    };

    options.spellCheck = {
      enabled: true,
      suggestionCount: 5,
      checkWordSpelling: (word, callback): void => {
        return this.spellcheckerService.checkWordSpelling(word, callback);
      },
      addWordToDictionary: (word): void =>
        this.spellcheckerService.addWordToDictionary(word),
    };
  }

  private configRibbon(options: Options): void {
    const fileTab: RibbonTab = options.ribbon.getTab(RibbonTabType.File)!;
    const insertTab: RibbonTab = options.ribbon.getTab(RibbonTabType.Insert)!;

    const pageLayoutTab: RibbonTab = options.ribbon.getTab(
      RibbonTabType.PageLayout
    )!;
    const referenceTab: RibbonTab = options.ribbon.getTab(
      RibbonTabType.References
    )!;
    options.ribbon.removeTab(referenceTab);

    fileTab!.removeItem(FileTabItemId.PrintDocument);
    fileTab!.removeItem(FileTabItemId.ExportDocument);
    fileTab!.removeItem(FileTabItemId.OpenDocument);

    const item: RibbonItem = new RibbonButtonItem('open', 'Open', {
      icon: 'dxre-icon-Open',
      showText: true,
    });

    fileTab!.insertItem(item, 0);

    if (this.innerLinksEnabled) {
      // Internal links are domain-specific, so the custom command is added
      // only to editor instances that explicitly opt in before construction.
      insertTab.insertItemAfter(
        new RibbonButtonItem(
          INSERT_INTERNAL_LINK_COMMAND,
          this.translateService.instant('shared.editor.innerLinks.buttonLabel'),
          {
            icon: 'dxre-icon-Hyperlink',
            showText: true,
            beginGroup: true,
          }
        ),
        InsertTabItemId.ShowHyperlinkDialog
      );
    }

    pageLayoutTab.removeItem(PageLayoutTabItemId.ChangePageColor);
  }
}
